/**
 * Terms data hook - manages terms fetching and term/week selection
 *
 * Handles:
 * - Fetching terms summary for a grade
 * - Deriving selectedTerm and selectedWeek from URL
 * - Cache invalidation when navigating back from lessons
 */

import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { REQUEST_STATUS } from "@ts/enums/RequestStatus";
import { termsApi } from "@api/termsApi";
import type { Term, Week } from "@ts/interfaces/terms/api/termsApiInterface";
import type { ErrorInterface } from "@ts/interfaces/ApiResponseInterface";

interface UseTermsDataParams {
  urlGradeCode: number | undefined;
  urlTermId: string | undefined;
  urlWeekId: string | undefined;
  lessonId: string | undefined;
}

interface UseTermsDataReturn {
  terms: Term[];
  termsStatus: REQUEST_STATUS;
  termsError: ErrorInterface | null;
  selectedTerm: Term | null;
  selectedWeek: Week | null;
  setSelectedTerm: (term: Term | null) => void;
  setSelectedWeek: (week: Week | null) => void;
  invalidateTermsCache: () => void;
  forGradeCode: number | undefined;
}

export function useTermsData({
  urlGradeCode,
  urlTermId,
  urlWeekId,
  lessonId
}: UseTermsDataParams): UseTermsDataReturn {
  const [terms, setTerms] = useState<Term[]>([]);
  const [termsStatus, setTermsStatus] = useState<REQUEST_STATUS>(REQUEST_STATUS.IDLE);
  const [termsError, setTermsError] = useState<ErrorInterface | null>(null);
  const [manualTerm, setManualTerm] = useState<Term | null>(null);
  const [manualWeek, setManualWeek] = useState<Week | null>(null);

  const lastFetchedGradeRef = useRef<number | undefined>(undefined);
  // Counter to force refetch when incremented (mirrors useSubjectsData).
  const [refetchCounter, setRefetchCounter] = useState(0);

  // Invalidate cache so next render cycle will refetch.
  //
  // Both lines below are necessary:
  //  • The ref reset defeats the same-grade early-return guard, which would
  //    otherwise see `lastFetchedGradeRef === urlGradeCode` and skip the
  //    fetch even though the effect re-ran.
  //  • The counter bump is what actually re-triggers the effect, since ref
  //    mutations alone don't cause React to re-run effects.
  //
  // Without the counter, callers that invalidate without also changing a URL
  // param (e.g. cross-tab sync via useCrossTabLessonSync) would clear the
  // ref but never cause the effect to re-run — leaving terms data stale.
  // This was the root cause of BLD2-524 (infinite spinner after cross-tab
  // invalidation) and BLD2-547 (stale completion status in other tabs).
  const invalidateTermsCache = useCallback(() => {
    lastFetchedGradeRef.current = undefined;
    setRefetchCounter(c => c + 1);
  }, []);

  // Fetch terms when the target grade changes or the cache is invalidated.
  useEffect(() => {
    if (!urlGradeCode) {
      setTerms([]);
      setTermsStatus(REQUEST_STATUS.IDLE);
      setTermsError(null);
      lastFetchedGradeRef.current = undefined;
      return;
    }

    // Already fetched for this grade during the current cache window.
    // Avoid depending on request status here: setting PENDING inside this
    // effect would otherwise retrigger the effect and fire a duplicate request
    // before the first response resolves.
    if (lastFetchedGradeRef.current === urlGradeCode) {
      return;
    }

    lastFetchedGradeRef.current = urlGradeCode;
    setTermsStatus(REQUEST_STATUS.PENDING);
    setTermsError(null);

    // Track whether this effect instance has been superseded so a late
    // response from a previous render doesn't overwrite newer data.
    let cancelled = false;

    termsApi.getTermsSummary(urlGradeCode)
      .then(response => {
        if (cancelled) return;
        // A malformed body must not reach state, or selectedTerm's .find()
        // would throw on the next render.
        setTerms(Array.isArray(response?.terms) ? response.terms : []);
        setTermsStatus(REQUEST_STATUS.SUCCESS);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const err = error as { customAttributes?: { httpErrorStatus?: number }; message?: string };
        setTermsError({
          code: err?.customAttributes?.httpErrorStatus ?? 0,
          message: err?.message ?? "Failed to fetch terms summary"
        });
        setTermsStatus(REQUEST_STATUS.ERROR);
        lastFetchedGradeRef.current = undefined;
      });

    return () => {
      cancelled = true;
      // If this effect was superseded before the response arrived, clear
      // the ref so the next effect run will retry the fetch instead of
      // skipping it via the same-grade dedup guard above.  Without this,
      // a rapid dependency change (e.g. lessonId flipping to undefined on
      // back-navigation) cancels the in-flight response but the new effect
      // instance sees the ref already set and early-returns — leaving
      // termsStatus stuck at PENDING forever.  See: BLD2-523.
      lastFetchedGradeRef.current = undefined;
    };
  }, [urlGradeCode, lessonId, urlTermId, refetchCounter]);

  // Clear manual selections when URL changes
  useEffect(() => {
    setManualTerm(null);
    setManualWeek(null);
  }, [urlTermId, urlWeekId]);

  // Derive selectedTerm from URL or manual selection.
  // During a background refetch (PENDING with existing data), keep returning
  // the current term so downstream consumers stay stable — otherwise
  // isDataReady flips to false, the lesson layout unmounts, startLesson
  // re-fires on remount, invalidateTermsCache triggers another refetch,
  // and the cycle repeats indefinitely.
  const selectedTerm = useMemo(() => {
    if (manualTerm) return manualTerm;
    if (!urlTermId) return null;
    // Block only on the initial load (no data yet), not on background refetches
    if (!Array.isArray(terms) || terms.length === 0) return null;
    return terms.find(t => t.id === urlTermId) ?? null;
  }, [manualTerm, urlTermId, terms]);

  // Derive selectedWeek from URL or manual selection
  const selectedWeek = useMemo(() => {
    if (manualWeek) return manualWeek;
    if (!urlWeekId || !selectedTerm) return null;
    return selectedTerm.weeks.find(w => w.id === urlWeekId) ?? null;
  }, [manualWeek, urlWeekId, selectedTerm]);

  return {
    terms,
    termsStatus,
    termsError,
    selectedTerm,
    selectedWeek,
    setSelectedTerm: setManualTerm,
    setSelectedWeek: setManualWeek,
    invalidateTermsCache,
    forGradeCode: lastFetchedGradeRef.current
  };
}
